import React, { Component } from 'react'
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { Badge, Button, Table } from 'reactstrap'
import PrimaryListingContainer from '../../Material/PrimaryListingContainer'
import BatchDetailsModal from './BatchDetailsModal'

const BadDebtCollectionBatchesQuery = gql`
    query BadDebtCollectionBatches($limit: Int) {
        badDebtCollectionBatches(limit: $limit) {
            id
            status
            dateAdded
            scheduleDatetime
            completedAt
            createdByName
            completedTasks
            totalTasks
            successfulTasks
            failedTasks
            totalAmountOwed
            totalAmountCollected
            taskResults {
                driverId
                driverPk
                driverName
                wasSuccessful
                amountAttempted
                amountCollected
                errorMessage
            }
        }
    }
`

class BadDebtBatchResultsList extends Component {
    constructor(props) {
        super(props)
        this.state = {
            selectedBatch: null,
        }
        this.pollingInterval = null
    }

    componentDidMount() {
        this.startPolling()
    }

    componentDidUpdate(prevProps) {
        // Restart polling if batches change (e.g., new batch added)
        if (prevProps.badDebtCollectionBatches !== this.props.badDebtCollectionBatches) {
            this.startPolling()
        }
    }

    componentWillUnmount() {
        this.stopPolling()
    }

    startPolling = () => {
        // Clear existing interval if any
        this.stopPolling()

        const batches = this.props.badDebtCollectionBatches || []

        // Only poll batches created in the last 60 minutes to avoid stuck batches
        const sixtyMinutesAgo = new Date(Date.now() - 60 * 60 * 1000)
        const hasRecentRunningBatches = batches.some((batch) => {
            if (batch.status !== 'RUNNING') return false

            // Check if batch was created recently
            const batchDate = new Date(batch.scheduleDatetime || batch.dateAdded)
            return batchDate > sixtyMinutesAgo
        })

        // Only poll if there are recent RUNNING batches
        if (hasRecentRunningBatches) {
            this.pollingInterval = setInterval(() => {
                const currentBatches = this.props.badDebtCollectionBatches || []
                const stillHasRecentRunningBatches = currentBatches.some((batch) => {
                    if (batch.status !== 'RUNNING') return false

                    const batchDate = new Date(batch.scheduleDatetime || batch.dateAdded)
                    return batchDate > sixtyMinutesAgo
                })

                if (stillHasRecentRunningBatches) {
                    // Use notifyOnNetworkStatusChange: false to prevent loading state flicker
                    this.props.refetch()
                } else {
                    // Stop polling when no recent running batches
                    this.stopPolling()
                }
            }, 10000) // Poll every 10 seconds
        }
    }

    stopPolling = () => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
        }
    }

    toggleBatchDetails = (batch) => {
        this.setState((prevState) => ({
            selectedBatch: prevState.selectedBatch?.id === batch.id ? null : batch,
        }))
    }

    formatDateTime = (value) => {
        if (!value) {
            return '--'
        }
        return new Date(value).toLocaleString()
    }

    formatCurrency = (value) => {
        return `$${Number(value || 0).toFixed(2)}`
    }

    getStatusColor = (status) => {
        const colorMap = {
            COMPLETED: 'success',
            RUNNING: 'info',
            PENDING: 'warning',
            CANCELED: 'secondary',
        }
        return colorMap[status] || 'secondary'
    }

    renderStatusBadge = (status) => {
        return <Badge color={this.getStatusColor(status)}>{status || '--'}</Badge>
    }

    render() {
        const { loading, badDebtCollectionBatches, refetch, error } = this.props
        const batches = badDebtCollectionBatches || []

        return (
            <>
                {error && (
                    <div className="alert alert-danger mb-3">
                        <strong>Error loading batch results:</strong> {error.message}
                        <Button
                            size="sm"
                            color="danger"
                            outline
                            className="ms-2"
                            onClick={() => refetch()}
                        >
                            Retry
                        </Button>
                    </div>
                )}
                <PrimaryListingContainer
                    listLoading={loading}
                    totalFetchedRows={batches.length}
                    refetchListQuery={refetch}
                >
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>Batch</th>
                                <th>Created</th>
                                <th>Completed</th>
                                <th>Created By</th>
                                <th>Status</th>
                                <th>Processed</th>
                                <th>Succeeded</th>
                                <th>Failed</th>
                                <th>Owed</th>
                                <th>Collected</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.length > 0 ? (
                                batches.map((batch) => {
                                    return (
                                        <tr key={batch.id}>
                                            <td>#{batch.id}</td>
                                            <td>{this.formatDateTime(batch.scheduleDatetime || batch.dateAdded)}</td>
                                            <td>{this.formatDateTime(batch.completedAt)}</td>
                                            <td>{batch.createdByName || '--'}</td>
                                            <td>{this.renderStatusBadge(batch.status)}</td>
                                            <td>
                                                {batch.completedTasks}/{batch.totalTasks}
                                            </td>
                                            <td>{batch.successfulTasks}</td>
                                            <td>{batch.failedTasks}</td>
                                            <td>{this.formatCurrency(batch.totalAmountOwed)}</td>
                                            <td>{this.formatCurrency(batch.totalAmountCollected)}</td>
                                            <td>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => this.toggleBatchDetails(batch)}
                                                >
                                                    View
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                })
                            ) : !loading ? (
                                <tr>
                                    <td colSpan={11} className="text-center text-muted py-4">
                                        <p className="mb-0">No bad debt batches found.</p>
                                        <small>Batches will appear here after you charge bad debt drivers.</small>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </PrimaryListingContainer>
                <BatchDetailsModal
                    isOpen={!!this.state.selectedBatch}
                    toggle={() => this.setState({ selectedBatch: null })}
                    batch={this.state.selectedBatch}
                />
            </>
        )
    }
}

export default graphql(BadDebtCollectionBatchesQuery, {
    options: () => ({
        variables: {
            limit: 30,
        },
        notifyOnNetworkStatusChange: false,
        fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { loading, badDebtCollectionBatches, refetch, error, networkStatus } }) => ({
        loading: loading && networkStatus === 1,
        badDebtCollectionBatches,
        refetch,
        error,
    }),
})(BadDebtBatchResultsList)
